import { Fragment, type JSX } from 'react';
import {
    Alert,
    Box,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';

import {
    ServerRequestState,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { DateTime } from 'luxon';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { Language, languageFromString } from 'dg-web-shared/lib/Localized.ts';
import * as SettingsState from '../common/state/SettingsState.ts';
import {
    LastUpdateTimestampAndRefresh,
    LastUpdateTimestampAndRefreshAsyncLoadedSection,
} from '../ui/LastUpdateTimestampAndRefresh.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import {
    DetailHeader,
    DetailTabs,
    HeaderBarWithRefreshButton,
    ParkingaboUserInfoTab,
    useParkingaboUserDetailOutletContext,
} from './OperatorParkingaboUsersDetail.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { operatorAppApiUrl } from '../api/Http.ts';
import { ParkingaboCustomerPaymentMode } from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';
import { numberToLocalPrice } from 'dg-web-shared/lib/NumberFormatter.ts';
import InfoIcon from '@mui/icons-material/Info';

interface ParkingaboAccountStatementResult {
    initialBalance: number;
    buckets: AccountStatementMonthBucket[];
}

interface AccountStatementMonthBucket {
    yearMonth: string;
    isCurrentMonth: boolean;
    items: AccountStatementItem[];
}

interface AccountStatementItem {
    date: string;
    remarks: string[];
    formattedPrice: string;
    formattedSaldo: string | null;
    occurrences: number;
    isError: boolean;
    firstOccurrence: string;
}

export function OperatorParkingaboUsersAccountStatement() {
    const { parkingaboUser } = useParkingaboUserDetailOutletContext();
    const { storeState } = useStore(s => ({
        language: languageFromString(
            new SettingsState.StateSlice(s).state.language,
        ),
    }));
    const [requestState, refetchList] = useServerFetch<
        ParkingaboAccountStatementResult,
        { customerNr: string; language: Language },
        null
    >(
        context => ({
            url: `/ui-api/operator-account/parkingabo/customer/${context.customerNr}/account-statement?lang=${context.language}`,
        }),
        {
            customerNr: parkingaboUser.customerNr,
            language: storeState.language,
        },
    );
    return (
        <OperatorRoutedModalContent
            header={<DetailHeader />}
            bar={
                <DetailTabs
                    parkingaboUserInfoTab={
                        ParkingaboUserInfoTab.ACCOUNT_STATEMTENT
                    }
                    roleHasAppPayment={
                        parkingaboUser.role.paymentMode ===
                        ParkingaboCustomerPaymentMode.APP_PAYMENT
                    }
                />
            }
            style={{ padding: '0px 0px' }}
            body={
                <>
                    <HeaderBarWithRefreshButton
                        refreshButton={
                            <LastUpdateTimestampAndRefreshAsyncLoadedSection
                                requestState={requestState}
                                onRefresh={refetchList}
                                render={(result, timestamp) => (
                                    <LastUpdateTimestampAndRefresh
                                        resultsCount={result.buckets.reduce(
                                            (sum, bucket) =>
                                                sum + bucket.items.length,
                                            0,
                                        )}
                                        eof
                                        onRefresh={refetchList}
                                        timestamp={timestamp}
                                    />
                                )}
                            />
                        }
                    />
                    <Box
                        style={{
                            flex: '1 1 0',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: 'auto',
                        }}
                    >
                        <OperatorParkingaboUsersAccountStatementTable
                            requestState={requestState}
                            customerNr={parkingaboUser.customerNr}
                            language={storeState.language}
                        />
                    </Box>
                </>
            }
        />
    );
}

function OperatorParkingaboUsersAccountStatementTable({
    requestState,
    customerNr,
    language,
}: {
    requestState: ServerRequestState<ParkingaboAccountStatementResult, null>;
    customerNr: string;
    language: Language;
}) {
    return (
        <OperatorAsyncLoadedSection
            requestState={requestState}
            render={result => {
                const buckets = result.buckets;

                if (buckets.length <= 0 && result.initialBalance == 0) {
                    return (
                        <Alert severity="info">
                            <Localized
                                de="Dieser Benutzer hat keine Bewegungen."
                                fr="Cet utilisateur n'a pas de mouvements."
                                it="Questo utente non ha movimenti."
                                en="This user has no movements."
                            />
                        </Alert>
                    );
                }

                return (
                    <TableContainer sx={{ flex: '1 1 0' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <Localized
                                            de="Datum"
                                            fr="Date"
                                            it="Data"
                                            en="Date"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Localized
                                            de="Beschreibung"
                                            fr="Description"
                                            it="Descrizione"
                                            en="Description"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <Localized
                                            de="Preis"
                                            fr="Prix"
                                            it="Prezzo"
                                            en="Price"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <Localized
                                            de="Saldo"
                                            fr="Solde"
                                            it="Saldo"
                                            en="Balance"
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {buckets.map(bucket => {
                                    return (
                                        <Fragment key={bucket.yearMonth}>
                                            <MonthHeader
                                                bucket={bucket}
                                                customerNr={customerNr}
                                                language={language}
                                            />
                                            {bucket.items.map(item => (
                                                <AccountStatementItemRow
                                                    key={item.date}
                                                    item={item}
                                                />
                                            ))}
                                        </Fragment>
                                    );
                                })}
                                {result.initialBalance != 0 && (
                                    <InitialBalanceRow
                                        initialBalance={result.initialBalance}
                                    />
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                );
            }}
        />
    );
}

function MonthHeader({
    bucket,
    customerNr,
    language,
}: {
    bucket: AccountStatementMonthBucket;
    customerNr: string;
    language: Language;
}) {
    const { operatorExternalId } = useOperatorContext();

    const key = bucket.yearMonth;

    const monthLabel = Formatter.monthNameYear(
        DateTime.fromISO(key),
        Formatter.getLocaleFromString(language),
    );

    const downloadUrl = urlWithOperatorExternalId(
        operatorAppApiUrl(
            `/parkingabo/customer/${customerNr}/account-statement/${key.replace('-', '/')}/pdf`,
        ),
        operatorExternalId,
    );

    return (
        <TableRow>
            <TableCell
                colSpan={4}
                sx={{ height: 30, padding: 0, verticalAlign: 'top' }}
            >
                <Stack
                    direction="row"
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        paddingX: 3,
                        paddingY: 0.5,
                        backgroundColor: theme => theme.palette.grey[200],
                        textTransform: 'uppercase',
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={theme => ({
                            color: theme.palette.blue.main,
                        })}
                    >
                        {monthLabel}
                    </Typography>
                    {!bucket.isCurrentMonth && (
                        <IconButton
                            size="small"
                            onClick={() => openNewTab(downloadUrl)}
                            sx={theme => ({
                                color: theme.palette.blue.main,
                            })}
                        >
                            <Download fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            </TableCell>
        </TableRow>
    );
}

function AccountStatementItemRow({ item }: { item: AccountStatementItem }) {
    return (
        <TableRow
            sx={theme => ({
                backgroundColor: item.isError
                    ? 'rgba(239, 83, 80,0.1)'
                    : theme.palette.background.default,
            })}
        >
            <TableCell
                sx={theme => ({
                    color: item.isError
                        ? 'rgb(239, 83, 80)'
                        : theme.palette.blue.main,
                })}
            >
                {DateTime.fromISO(item.firstOccurrence).toFormat(
                    'dd.MM.yyyy – HH:mm',
                )}
            </TableCell>
            <TableCell
                sx={theme => ({
                    color: item.isError
                        ? 'rgb(239, 83, 80)'
                        : theme.palette.blue.main,
                })}
            >
                {formatDescription(item)}
            </TableCell>
            <TableCell
                sx={theme => ({
                    textAlign: 'right',
                    fontStyle: item.isError ? 'italic' : '',
                    color: item.isError
                        ? 'rgb(239, 83, 80)'
                        : theme.palette.blue.main,
                })}
            >
                {item.formattedPrice}
            </TableCell>
            <TableCell
                sx={theme => ({
                    textAlign: 'right',
                    color: item.isError
                        ? 'rgb(239, 83, 80)'
                        : theme.palette.blue.main,
                })}
            >
                {item.formattedSaldo}
            </TableCell>
        </TableRow>
    );
}

function InitialBalanceRow({ initialBalance }: { initialBalance: number }) {
    return (
        <TableRow
            sx={{
                verticalAlign: 'center',
                alignItems: 'center',
                width: '100%',
                backgroundColor: theme => theme.palette.grey[200],
                textTransform: 'uppercase',
            }}
        >
            <TableCell
                colSpan={3}
                sx={{
                    height: 30,
                    padding: 0,
                    paddingX: 3,
                }}
            >
                <Stack direction="row">
                    <Typography
                        variant="subtitle2"
                        sx={theme => ({
                            color: theme.palette.blue.main,
                        })}
                    >
                        <Localized
                            de="Saldovortrag"
                            fr="Report de solde"
                            it="Saldo riportato"
                            en="Balance carried forward"
                        />
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip
                            title={
                                <Localized
                                    de="Saldo aus Bewegungen, die älter als 18 Monate sind (Details nicht mehr verfügbar)."
                                    fr="Solde résultant de mouvements datant de plus de 18 mois (détails non disponibles)."
                                    it="Saldo risultante da movimenti più vecchi di 18 mesi (dettagli non più disponibili)."
                                    en="Balance resulting from movements older than 18 months (details no longer available)."
                                />
                            }
                            placement="right"
                        >
                            <InfoIcon
                                sx={{
                                    fontSize: 16,
                                    marginLeft: 1,
                                }}
                            />
                        </Tooltip>
                    </Box>
                </Stack>
            </TableCell>
            <TableCell
                sx={{
                    textAlign: 'right',
                    height: 30,
                    padding: 0,
                    paddingX: 3,
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={theme => ({
                        color: theme.palette.blue.main,
                    })}
                >
                    {numberToLocalPrice(initialBalance)}
                </Typography>
            </TableCell>
        </TableRow>
    );
}

function formatDescription(item: AccountStatementItem): JSX.Element {
    return (
        <>
            <strong>{item.remarks[0]}</strong>
            {item.remarks.slice(1).map(line => (
                <Fragment key={line}>
                    <br />
                    {line}
                </Fragment>
            ))}
            {item.occurrences > 1 && (
                <>
                    <br />
                    <em>
                        <Localized
                            de={`${item.occurrences} mal wiederholt`}
                            fr={`répété ${item.occurrences} fois`}
                            it={`ripetuto ${item.occurrences} volte`}
                            en={`repeated ${item.occurrences} times`}
                        />
                    </em>
                    <br />
                    <em>
                        <Localized
                            de={`(zuletzt am ${DateTime.fromISO(
                                item.date,
                            ).toFormat('dd.MM.yyyy – HH:mm')})`}
                            fr={`(dernier le ${DateTime.fromISO(
                                item.date,
                            ).toFormat('dd.MM.yyyy – HH:mm')})`}
                            it={`(ultima volta il ${DateTime.fromISO(
                                item.date,
                            ).toFormat('dd.MM.yyyy – HH:mm')})`}
                            en={`(last on ${DateTime.fromISO(
                                item.date,
                            ).toFormat('dd.MM.yyyy – HH:mm')})`}
                        />
                    </em>
                </>
            )}
        </>
    );
}
